/**
 * Clipboard text format (W1-10b core-commands-motion; DESIGN.md §5.4 step 1).
 *
 * The internal payload is also written to the system clipboard as text: `PHIEDIT-CLIP/1 ` followed by the payload
 * as JSON (Beats as {w, n, d} objects, values and eases as in the document model), so another tab, project or
 * difficulty can paste it. Text read back from the system clipboard is untrusted: `parseClipText` validates every
 * field against the model's record shapes (core/model/schema.ts) and the model rules a paste relies on (normalised
 * beats, values of the property's type, valid eases, strictly increasing key beats, holds ending at or after their
 * start, slots in range, an acyclic parent structure of whole lines) and returns null for anything else.
 */

import { Beats } from '../time/beat';
import { isPlainObject } from '../util/json';
import { assetShape, checkShape, easeShape, lineShape, noteShape } from '../model/schema';
import { isTrackValueOf } from '../model/invariants';
import { isLineProp, PROP_SPECS } from '../model/props';
import type { Ease } from '../ease/types';
import type { LineProp } from '../model/types';
import type { ClipboardPayload } from './types';

export const CLIP_PREFIX = 'PHIEDIT-CLIP/1';
export const CLIP_FORMAT = 'phiedit-clip/1';

export function serializeClip(p: ClipboardPayload): string {
  return `${CLIP_PREFIX} ${JSON.stringify(p)}`;
}

type Rec = Readonly<Record<string, unknown>>;

function isBeat(v: unknown): boolean {
  if (!isPlainObject(v)) return false;
  const b = v;
  const w = b.w;
  const n = b.n;
  const d = b.d;
  if (typeof w !== 'number' || typeof n !== 'number' || typeof d !== 'number') return false;
  return Beats.isNormal({ w, n, d });
}

function beatOf(v: unknown): { readonly w: number; readonly n: number; readonly d: number } {
  return v as { readonly w: number; readonly n: number; readonly d: number };
}

function isNonNegInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isSafeInteger(v) && v >= 0;
}

function easeOk(v: unknown, prop: LineProp): boolean {
  if (checkShape(easeShape, v, '', 1).length > 0) return false;
  const e = v as Ease;
  if (e.kind === 'tween') return e.id >= 1 && e.id <= 29 && e.left >= 0 && e.left < e.right && e.right <= 1;
  if (e.kind === 'bezier') return prop !== 'speed';
  return true;
}

function keysOk(keys: unknown, prop: LineProp): boolean {
  if (!Array.isArray(keys) || keys.length === 0) return false;
  const type = PROP_SPECS[prop].valueType;
  let prev: unknown = null;
  for (const k of keys as unknown[]) {
    if (!isPlainObject(k)) return false;
    const r = k;
    if (Object.keys(r).some((f) => !['t', 'vIn', 'vOut', 'ease', 'link'].includes(f))) return false;
    if (!isBeat(r.t) || !isTrackValueOf(type, r.vIn) || !isTrackValueOf(type, r.vOut)) return false;
    if (!easeOk(r.ease, prop) || !isNonNegInt(r.link)) return false;
    if (prev !== null && !Beats.lt(beatOf(prev), beatOf(r.t))) return false;
    prev = r.t;
  }
  return true;
}

function trackSliceOk(v: unknown, lineCount: number, slotted: boolean): boolean {
  if (!isPlainObject(v)) return false;
  const r = v;
  const allowed = slotted ? ['lineSlot', 'prop', 'layer', 'keys'] : ['prop', 'layer', 'keys'];
  if (Object.keys(r).some((f) => !allowed.includes(f))) return false;
  if (slotted && (!isNonNegInt(r.lineSlot) || r.lineSlot >= lineCount)) return false;
  if (!isLineProp(r.prop) || !isNonNegInt(r.layer)) return false;
  if (!PROP_SPECS[r.prop].layered && r.layer !== 0) return false;
  return keysOk(r.keys, r.prop);
}

function noteOk(v: unknown, withSrc: boolean): boolean {
  if (!isPlainObject(v)) return false;
  const r = v;
  if (!withSrc && 'src' in r && r.src !== undefined) return false;
  if (checkShape(noteShape, { ...r, id: 1, line: 1, gen: null }, '', 1).length > 0) return false;
  if ('id' in r || 'line' in r || 'gen' in r) return false;
  const t = beatOf(r.t);
  const end = beatOf(r.end);
  return r.kind === 'hold' ? Beats.lte(t, end) : Beats.eq(t, end);
}

function lineOk(v: unknown, index: number, count: number, parents: (number | null)[]): boolean {
  if (!isPlainObject(v)) return false;
  const r = v;
  const allowed = ['line', 'parentSlot', 'groupName', 'tracks', 'notes', 'assets', 'assetRefs'];
  if (Object.keys(r).some((f) => !allowed.includes(f))) return false;
  const line = r.line;
  if (!isPlainObject(line) || 'id' in line || 'parent' in line || 'group' in line) return false;
  if (checkShape(lineShape, { ...line, id: 1, parent: null, group: null }, '', 1).length > 0) return false;
  const slot = r.parentSlot;
  if (slot !== null && (!isNonNegInt(slot) || slot >= count || slot === index)) return false;
  parents.push(slot);
  if (r.groupName !== null && typeof r.groupName !== 'string') return false;
  if (!Array.isArray(r.tracks) || !Array.isArray(r.notes) || !Array.isArray(r.assets)) return false;
  const owners = new Set<string>();
  for (const t of r.tracks as unknown[]) {
    if (!trackSliceOk(t, 0, false)) return false;
    const tr = t as Rec;
    const key = `${String(tr.prop)}:${String(tr.layer)}`;
    if (owners.has(key)) return false;
    owners.add(key);
  }
  if (!(r.notes as unknown[]).every((n) => noteOk(n, true))) return false;
  if (!(r.assets as unknown[]).every((a) => typeof a === 'string' && a.length > 0)) return false;
  // Asset records of the source project (optional): valid records of listed assets only.
  if (r.assetRefs === undefined) return true;
  if (!Array.isArray(r.assetRefs)) return false;
  const listed = new Set(r.assets as string[]);
  return (r.assetRefs as unknown[]).every(
    (a) => isPlainObject(a) && listed.has(a.id as string) && checkShape(assetShape, a, '', 1).length === 0,
  );
}

/** True when following parent slots never loops. */
function acyclic(parents: readonly (number | null)[]): boolean {
  for (let i = 0; i < parents.length; i++) {
    let cur: number | null = parents[i] ?? null;
    let steps = 0;
    while (cur !== null) {
      if (cur === i || steps++ > parents.length) return false;
      cur = parents[cur] ?? null;
    }
  }
  return true;
}

/** Validates an unknown value as a clipboard payload. */
export function validatePayload(v: unknown): ClipboardPayload | null {
  if (!isPlainObject(v)) return null;
  const r = v;
  const allowed = ['format', 'origin', 'span', 'notes', 'tracks', 'lines', 'wholeLines'];
  if (Object.keys(r).some((f) => !allowed.includes(f))) return null;
  if (r.format !== CLIP_FORMAT || !isBeat(r.origin) || !isBeat(r.span)) return null;
  if (Beats.cmp(beatOf(r.span), Beats.ZERO) < 0) return null;
  const { lines, notes, tracks, wholeLines } = r;
  if (!Array.isArray(lines) || !Array.isArray(notes) || !Array.isArray(tracks) || !Array.isArray(wholeLines))
    return null;
  for (const l of lines as unknown[]) {
    if (!isPlainObject(l)) return null;
    const lr = l;
    if (Object.keys(lr).some((f) => f !== 'sourceLine' && f !== 'name')) return null;
    if (!isNonNegInt(lr.sourceLine) || typeof lr.name !== 'string') return null;
  }
  for (const n of notes as unknown[]) {
    if (!isPlainObject(n)) return null;
    const nr = n;
    if (Object.keys(nr).some((f) => f !== 'lineSlot' && f !== 'note')) return null;
    if (!isNonNegInt(nr.lineSlot) || nr.lineSlot >= lines.length || !noteOk(nr.note, false)) return null;
  }
  for (const t of tracks as unknown[]) if (!trackSliceOk(t, lines.length, true)) return null;
  const parents: (number | null)[] = [];
  for (let i = 0; i < wholeLines.length; i++) if (!lineOk(wholeLines[i], i, wholeLines.length, parents)) return null;
  if (!acyclic(parents)) return null;
  return v as unknown as ClipboardPayload;
}

/** Parses system clipboard text in our format; null for anything else. */
export function parseClipText(text: string): ClipboardPayload | null {
  const s = text.trim();
  if (!s.startsWith(CLIP_PREFIX)) return null;
  const body = s.slice(CLIP_PREFIX.length).trim();
  let json: unknown;
  try {
    json = JSON.parse(body);
  } catch {
    return null;
  }
  return validatePayload(json);
}
